import { motion } from 'framer-motion';
import { Shield, Lock, EyeOff, Server, FileCheck, CheckCircle2 } from 'lucide-react';

export default function Security() {
  const securityFeatures = [
    {
      icon: Lock,
      title: 'End-to-End Encryption',
      description: 'All identity data is encrypted using AES-256-GCM at rest and TLS 1.3 in transit. Your documents are protected from the moment they are uploaded.',
    },
    {
      icon: EyeOff,
      title: 'Data Minimization',
      description: 'We only store what is absolutely necessary. All uploaded images are purged after 24 hours once the verification processing is complete.',
    },
    {
      icon: Server,
      title: 'Local Data Residency',
      description: 'In compliance with the Philippine Data Privacy Act, all sensitive PII is stored in secure local nodes within the Philippines.',
    },
    {
      icon: FileCheck,
      title: 'Cryptographic Audit Trail',
      description: 'Every verification event generates an immutable SHA-256 hash recorded for enterprise auditing and compliance verification.',
    },
    {
      icon: Shield,
      title: 'Zero-Trust Mobile Handoff',
      description: 'Desktop-to-Mobile handoffs are secured by single-use, 15-minute ephemeral tokens. Data uploads directly to secure storage, bypassing the desktop client.',
    },
    {
      icon: CheckCircle2,
      title: 'Forensic ID Validation',
      description: 'We utilize 2D barcode cryptography (PDF417 & QR) to cryptographically validate data origin, detecting modified credentials that pass visual inspection.',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-teal-950/20 to-slate-900 py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <Shield className="w-16 h-16 text-teal-500 mx-auto mb-6" />
          <h1 className="text-4xl font-bold text-white mb-4">Security Whitepaper</h1>
          <p className="text-xl text-gray-400">
            Learn how Agos Identity protects sensitive data and ensures regulatory compliance.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {securityFeatures.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-slate-800/40 p-8 rounded-2xl border border-slate-700/50"
            >
              <feature.icon className="w-8 h-8 text-teal-400 mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
              <p className="text-gray-400 leading-relaxed">{feature.description}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="bg-teal-600/10 border border-teal-500/20 p-8 rounded-3xl"
        >
          <h2 className="text-2xl font-bold text-white mb-6">NPC & BSP Compliance</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-1 shrink-0" />
              <p className="text-gray-300">
                <span className="text-white font-medium">RA 10173 (Data Privacy Act of 2012):</span> Fully aligned with Philippine national standards for data protection and privacy.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-1 shrink-0" />
              <p className="text-gray-300">
                <span className="text-white font-medium">BSP eKYC Standards:</span> Engineered to meet the rigorous requirements for digital banking and financial institutions.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-1 shrink-0" />
              <p className="text-gray-300">
                <span className="text-white font-medium">ISO 27001 Alignment:</span> Our operational processes are designed following global best practices for information security management.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
